import type { XYPosition } from "@xyflow/react";

export const COMPONENT_SNAP_GAP = 12;
export const COMPONENT_SNAP_GAP_MIN = 4;
export const COMPONENT_SNAP_GAP_MAX = 32;
export const COMPONENT_SNAP_THRESHOLD_PX = 10;
const SNAP_INDEX_CELL_SIZE = 512;

export interface SnapRect extends XYPosition {
  id: string;
  width: number;
  height: number;
}

export interface SnapGuide {
  axis: "x" | "y";
  position: number;
  start: number;
  end: number;
  kind: "alignment" | "spacing";
}

export interface SnapResult {
  position: XYPosition;
  snappedX: boolean;
  snappedY: boolean;
  guides: SnapGuide[];
}

export interface ResizeSnapResult {
  rect: Omit<SnapRect, "id">;
  snappedX: boolean;
  snappedY: boolean;
  guides: SnapGuide[];
}

export interface ResizeEdges {
  horizontal: "left" | "right" | null;
  vertical: "top" | "bottom" | null;
}

interface IndexedGuide {
  value: number;
  rect: SnapRect;
}

interface AxisCandidate {
  value: number;
  distance: number;
  orthogonalDistance: number;
  guide: SnapGuide;
}

/** A drag-session spatial/alignment index built once and queried on every pointer move. */
export class NodeSnapIndex {
  private readonly buckets = new Map<string, SnapRect[]>();
  private readonly xGuides: IndexedGuide[] = [];
  private readonly yGuides: IndexedGuide[] = [];

  constructor(rects: readonly SnapRect[]) {
    for (const rect of rects) {
      const minX = Math.floor(rect.x / SNAP_INDEX_CELL_SIZE);
      const maxX = Math.floor((rect.x + rect.width) / SNAP_INDEX_CELL_SIZE);
      const minY = Math.floor(rect.y / SNAP_INDEX_CELL_SIZE);
      const maxY = Math.floor((rect.y + rect.height) / SNAP_INDEX_CELL_SIZE);
      for (let cellY = minY; cellY <= maxY; cellY += 1) {
        for (let cellX = minX; cellX <= maxX; cellX += 1) {
          const key = `${cellX}:${cellY}`;
          const bucket = this.buckets.get(key);
          if (bucket) bucket.push(rect);
          else this.buckets.set(key, [rect]);
        }
      }
      this.xGuides.push(
        { value: rect.x, rect },
        { value: rect.x + rect.width / 2, rect },
        { value: rect.x + rect.width, rect },
      );
      this.yGuides.push(
        { value: rect.y, rect },
        { value: rect.y + rect.height / 2, rect },
        { value: rect.y + rect.height, rect },
      );
    }
    this.xGuides.sort((left, right) => left.value - right.value);
    this.yGuides.sort((left, right) => left.value - right.value);
  }

  nearby(rect: Omit<SnapRect, "id">, padding: number): SnapRect[] {
    const minX = Math.floor((rect.x - padding) / SNAP_INDEX_CELL_SIZE);
    const maxX = Math.floor((rect.x + rect.width + padding) / SNAP_INDEX_CELL_SIZE);
    const minY = Math.floor((rect.y - padding) / SNAP_INDEX_CELL_SIZE);
    const maxY = Math.floor((rect.y + rect.height + padding) / SNAP_INDEX_CELL_SIZE);
    const seen = new Set<string>();
    const result: SnapRect[] = [];
    for (let cellY = minY; cellY <= maxY; cellY += 1) {
      for (let cellX = minX; cellX <= maxX; cellX += 1) {
        for (const target of this.buckets.get(`${cellX}:${cellY}`) ?? []) {
          if (seen.has(target.id)) continue;
          seen.add(target.id);
          result.push(target);
        }
      }
    }
    return result;
  }

  /** Finds distant neighbours sharing a near left/center/right or top/middle/bottom guide. */
  aligned(rect: Omit<SnapRect, "id">, threshold: number): SnapRect[] {
    const seen = new Set<string>();
    const result: SnapRect[] = [];
    const collect = (entries: IndexedGuide[], values: readonly number[]) => {
      for (const value of values) {
        let entryIndex = lowerBound(entries, value - threshold);
        while (entryIndex < entries.length && entries[entryIndex].value <= value + threshold) {
          const target = entries[entryIndex].rect;
          if (!seen.has(target.id)) {
            seen.add(target.id);
            result.push(target);
          }
          entryIndex += 1;
        }
      }
    };
    collect(this.xGuides, [rect.x, rect.x + rect.width / 2, rect.x + rect.width]);
    collect(this.yGuides, [rect.y, rect.y + rect.height / 2, rect.y + rect.height]);
    return result;
  }

  candidates(rect: Omit<SnapRect, "id">, threshold: number, gap: number): SnapRect[] {
    const result = this.nearby(rect, threshold + gap);
    const seen = new Set(result.map((target) => target.id));
    for (const target of this.aligned(rect, threshold)) {
      if (!seen.has(target.id)) result.push(target);
    }
    return result;
  }
}

/**
 * Snaps spaced adjacent edges and common left/center/right or top/middle/bottom
 * guides. Axes remain independent so a component can settle into a corner.
 */
export function snapNodePosition(
  moving: Pick<SnapRect, "width" | "height">,
  position: XYPosition,
  index: NodeSnapIndex,
  threshold: number,
  gap = COMPONENT_SNAP_GAP,
): SnapResult {
  const rect = { ...position, width: moving.width, height: moving.height };
  const targets = index.candidates(rect, threshold, gap);
  let bestX: AxisCandidate | null = null;
  let bestY: AxisCandidate | null = null;

  for (const target of targets) {
    const verticalDistance = rangeDistance(rect.y, rect.y + rect.height, target.y, target.y + target.height);
    const horizontalDistance = rangeDistance(rect.x, rect.x + rect.width, target.x, target.x + target.width);

    if (verticalDistance === 0) {
      bestX = chooseCandidate(bestX, axisCandidate(
        target.x + target.width + gap,
        position.x,
        verticalDistance,
        spacingGuide("x", target.x + target.width + gap / 2, rect, target),
      ), threshold);
      bestX = chooseCandidate(bestX, axisCandidate(
        target.x - gap - moving.width,
        position.x,
        verticalDistance,
        spacingGuide("x", target.x - gap / 2, rect, target),
      ), threshold);
    }

    const targetXGuides = [target.x, target.x + target.width / 2, target.x + target.width];
    const movingXOffsets = [0, moving.width / 2, moving.width];
    for (let guideIndex = 0; guideIndex < targetXGuides.length; guideIndex += 1) {
      const value = targetXGuides[guideIndex] - movingXOffsets[guideIndex];
      const snappedRect = { ...rect, x: value };
      bestX = chooseCandidate(bestX, axisCandidate(
        value,
        position.x,
        verticalDistance,
        alignmentGuide("x", targetXGuides[guideIndex], snappedRect, target),
      ), threshold);
    }

    if (horizontalDistance === 0) {
      bestY = chooseCandidate(bestY, axisCandidate(
        target.y + target.height + gap,
        position.y,
        horizontalDistance,
        spacingGuide("y", target.y + target.height + gap / 2, rect, target),
      ), threshold);
      bestY = chooseCandidate(bestY, axisCandidate(
        target.y - gap - moving.height,
        position.y,
        horizontalDistance,
        spacingGuide("y", target.y - gap / 2, rect, target),
      ), threshold);
    }

    const targetYGuides = [target.y, target.y + target.height / 2, target.y + target.height];
    const movingYOffsets = [0, moving.height / 2, moving.height];
    for (let guideIndex = 0; guideIndex < targetYGuides.length; guideIndex += 1) {
      const value = targetYGuides[guideIndex] - movingYOffsets[guideIndex];
      const snappedRect = { ...rect, y: value };
      bestY = chooseCandidate(bestY, axisCandidate(
        value,
        position.y,
        horizontalDistance,
        alignmentGuide("y", targetYGuides[guideIndex], snappedRect, target),
      ), threshold);
    }
  }

  return {
    position: { x: bestX?.value ?? position.x, y: bestY?.value ?? position.y },
    snappedX: Boolean(bestX),
    snappedY: Boolean(bestY),
    guides: [bestX?.guide, bestY?.guide].filter((guide): guide is SnapGuide => Boolean(guide)),
  };
}

/** Snaps the actively resized boundaries while keeping their opposite sides fixed. */
export function snapNodeResize(
  current: Omit<SnapRect, "id">,
  proposed: Omit<SnapRect, "id">,
  edges: ResizeEdges,
  index: NodeSnapIndex,
  threshold: number,
  gap = COMPONENT_SNAP_GAP,
): ResizeSnapResult {
  const targets = index.candidates(proposed, threshold, gap);
  let bestX: AxisCandidate | null = null;
  let bestY: AxisCandidate | null = null;
  const movingX = edges.horizontal === "left" ? proposed.x : proposed.x + proposed.width;
  const movingY = edges.vertical === "top" ? proposed.y : proposed.y + proposed.height;

  for (const target of targets) {
    const verticalDistance = rangeDistance(proposed.y, proposed.y + proposed.height, target.y, target.y + target.height);
    const horizontalDistance = rangeDistance(proposed.x, proposed.x + proposed.width, target.x, target.x + target.width);
    if (edges.horizontal) {
      for (const guide of [target.x, target.x + target.width / 2, target.x + target.width]) {
        bestX = chooseCandidate(bestX, axisCandidate(
          guide,
          movingX,
          verticalDistance,
          alignmentGuide("x", guide, proposed, target),
        ), threshold);
      }
      if (verticalDistance === 0) {
        for (const boundary of [target.x - gap, target.x + target.width + gap]) {
          bestX = chooseCandidate(bestX, axisCandidate(
            boundary,
            movingX,
            verticalDistance,
            spacingGuide("x", boundary < target.x ? target.x - gap / 2 : target.x + target.width + gap / 2, proposed, target),
          ), threshold);
        }
      }
    }

    if (edges.vertical) {
      for (const guide of [target.y, target.y + target.height / 2, target.y + target.height]) {
        bestY = chooseCandidate(bestY, axisCandidate(
          guide,
          movingY,
          horizontalDistance,
          alignmentGuide("y", guide, proposed, target),
        ), threshold);
      }
      if (horizontalDistance === 0) {
        for (const boundary of [target.y - gap, target.y + target.height + gap]) {
          bestY = chooseCandidate(bestY, axisCandidate(
            boundary,
            movingY,
            horizontalDistance,
            spacingGuide("y", boundary < target.y ? target.y - gap / 2 : target.y + target.height + gap / 2, proposed, target),
          ), threshold);
        }
      }
    }
  }

  const rect = { ...proposed };
  if (bestX && edges.horizontal === "left") {
    const right = current.x + current.width;
    rect.x = bestX.value;
    rect.width = right - bestX.value;
  } else if (bestX && edges.horizontal === "right") {
    rect.width = bestX.value - current.x;
  }
  if (bestY && edges.vertical === "top") {
    const bottom = current.y + current.height;
    rect.y = bestY.value;
    rect.height = bottom - bestY.value;
  } else if (bestY && edges.vertical === "bottom") {
    rect.height = bestY.value - current.y;
  }

  return {
    rect,
    snappedX: Boolean(bestX),
    snappedY: Boolean(bestY),
    guides: [bestX?.guide, bestY?.guide].filter((guide): guide is SnapGuide => Boolean(guide)),
  };
}

function lowerBound(entries: readonly IndexedGuide[], value: number): number {
  let low = 0;
  let high = entries.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (entries[middle].value < value) low = middle + 1;
    else high = middle;
  }
  return low;
}

function rangeDistance(startA: number, endA: number, startB: number, endB: number): number {
  if (endA < startB) return startB - endA;
  if (endB < startA) return startA - endB;
  return 0;
}

function axisCandidate(
  value: number,
  current: number,
  orthogonalDistance: number,
  guide: SnapGuide,
): AxisCandidate {
  return { value, distance: Math.abs(value - current), orthogonalDistance, guide };
}

function chooseCandidate(
  current: AxisCandidate | null,
  candidate: AxisCandidate,
  threshold: number,
): AxisCandidate | null {
  if (candidate.distance > threshold) return current;
  if (!current) return candidate;
  if (candidate.guide.kind !== current.guide.kind) {
    return candidate.guide.kind === "spacing" ? candidate : current;
  }
  if (candidate.distance < current.distance - 0.001) return candidate;
  if (Math.abs(candidate.distance - current.distance) <= 0.001
    && candidate.orthogonalDistance < current.orthogonalDistance) return candidate;
  return current;
}

function alignmentGuide(
  axis: "x" | "y",
  position: number,
  moving: Omit<SnapRect, "id">,
  target: SnapRect,
): SnapGuide {
  return axis === "x"
    ? { axis, position, start: Math.min(moving.y, target.y), end: Math.max(moving.y + moving.height, target.y + target.height), kind: "alignment" }
    : { axis, position, start: Math.min(moving.x, target.x), end: Math.max(moving.x + moving.width, target.x + target.width), kind: "alignment" };
}

function spacingGuide(
  axis: "x" | "y",
  position: number,
  moving: Omit<SnapRect, "id">,
  target: SnapRect,
): SnapGuide {
  return axis === "x"
    ? { axis, position, start: Math.max(moving.y, target.y), end: Math.min(moving.y + moving.height, target.y + target.height), kind: "spacing" }
    : { axis, position, start: Math.max(moving.x, target.x), end: Math.min(moving.x + moving.width, target.x + target.width), kind: "spacing" };
}
